/**
 * Numbers, money and dates as an Italian agency reads them.
 *
 * One place for the formatters, built once: an `Intl` formatter is not cheap
 * to construct, and about twenty-five screens each kept their own copy — some
 * built a new one per cell — with small differences nobody chose.
 */

const LOCALE = 'it-IT';

const wholeEuros = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});
const euros = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'EUR' });
const fineEuros = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 4,
});
const numbers = new Intl.NumberFormat(LOCALE);

const dateTimes = {
  medium: new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium', timeStyle: 'short' }),
  short: new Intl.DateTimeFormat(LOCALE, { dateStyle: 'short', timeStyle: 'short' }),
};
const dates = {
  medium: new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium' }),
  numeric: new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' }),
};
const times = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit' });
const months = new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric' });
const dayMonths = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit' });

type Instant = string | number | Date;
const toDate = (value: Instant): Date => (value instanceof Date ? value : new Date(value));

/**
 * A price or a budget, in whole euros. The API sends `numeric` columns as
 * strings, so both are accepted; `null` is a price nobody set.
 */
export function formatPrice(amount: number | string | null, empty = '—'): string {
  return amount === null || amount === '' ? empty : wholeEuros.format(Number(amount));
}

/** Euros to the cent. */
export function formatEuro(amount: number): string {
  return euros.format(amount);
}

/** A cost recorded in cents (four decimals, never rounded on the way in), to the cent. */
export function formatCents(cents: number): string {
  return euros.format(cents / 100);
}

/** A unit price in cents — per million tokens — with the fractions a comparison needs. */
export function formatUnitCents(cents: number | null): string {
  return cents === null ? '—' : fineEuros.format(cents / 100);
}

export function formatNumber(value: number): string {
  return numbers.format(value);
}

/** Date and time: `1 ott 2026, 09:30`, or `01/10/26, 09:30` where room is short. */
export function formatDateTime(value: Instant, style: 'medium' | 'short' = 'medium'): string {
  return dateTimes[style].format(toDate(value));
}

/** A day: `1 ott 2026`, or `01/10/2026`. */
export function formatDate(value: Instant, style: 'medium' | 'numeric' = 'medium'): string {
  return dates[style].format(toDate(value));
}

/** `09:30`. */
export function formatTime(value: Instant): string {
  return times.format(toDate(value));
}

/** `ottobre 2026`. */
export function formatMonth(value: Instant): string {
  return months.format(toDate(value));
}

/** `01/10`, for chart axes. */
export function formatDayMonth(value: Instant): string {
  return dayMonths.format(toDate(value));
}

/**
 * A value from a free attribute bag — a property's attributes, a demand's
 * preferences. The bags hold scalars only (ADR-0005), so nothing here ever has
 * to stringify an object.
 */
export function formatScalar(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Sì' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value === '' ? '—' : value;
  return '—';
}

/** `Via Dante 1`: the street and the number, the way every screen writes an address line. */
export function streetLine(street: string | null, number: string | null): string {
  return [street, number].filter(Boolean).join(' ');
}
